import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TableSortLabel,
  Box,
  IconButton,
  Typography,
  Button,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import EmailIcon from "@mui/icons-material/Email";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  TABLE_STYLES,
  TABLE_ROW_HOVER_SX,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";

import { formatPhone } from "../../../utils/Phone";
import normalizeJobTitle from "../../../utils/jobTitleNormalizer";

const BRAND_BLUE = "#00336C";
const NAME_COLUMN_WIDTH = 280;
const COMPANY_COLUMN_WIDTH = 240;
const JOB_TITLE_COLUMN_WIDTH = 200;
const PHONE_COLUMN_WIDTH = 150;
const EMAIL_COLUMN_WIDTH = 90;
const PROFILE_COLUMN_WIDTH = 90;

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

function ProspectingContactsTable({
  embedded,
  paginatedContacts,
  hasContacts,
  selectedIds,
  allSelected,
  someSelected,
  toggleOne,
  togglePage,
  navigate,
  orderBy,
  orderDirection,
  onSort,
  companyFilter,
  onCompanyFilterChange,
  uniqueCompanyNames,
  jobTitleFilter,
  onJobTitleFilterChange,
  uniqueJobTitles,
  setActiveContact,
  setProfileOpen,
  user,
  addRecentItem,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const openFilterMenu = (event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  };

  const closeFilterMenu = () => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  };

  const filteredCompanyOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return uniqueCompanyNames;
    return uniqueCompanyNames.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, uniqueCompanyNames]);

  const filteredJobTitleOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return uniqueJobTitles;
    return uniqueJobTitles.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, uniqueJobTitles]);

  const renderFilterHeader = ({
    label,
    filterKey,
    sortable = false,
    sortKey = filterKey,
    active = false,
  }) => {
    const minWidthByFilterKey = {
      company: 132,
      job_title: 140,
    };

    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Button
            variant="text"
            onClick={(event) => openFilterMenu(event, filterKey)}
            sx={{
              ...HEADER_TRIGGER_SX,
              minWidth: minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
              color: active ? "#648315" : BRAND_BLUE,
              border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
              backgroundColor: "rgba(255,255,255,0.92)",
              "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
            }}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
          >
            {label}
          </Button>
          {sortable && (
            <TableSortLabel
              active={orderBy === sortKey}
              direction={orderBy === sortKey ? orderDirection : "asc"}
              onClick={() => onSort(sortKey)}
              sx={{
                position: "absolute",
                left: "100%",
                top: "50%",
                transform: "translateY(-50%)",
                ml: 0.5,
                "& .MuiTableSortLabel-icon": {
                  fontSize: "1rem",
                },
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: BRAND_BLUE }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "company") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search company")}
          <MenuItem
            selected={!companyFilter}
            onClick={() => {
              onCompanyFilterChange("");
              closeFilterMenu();
            }}
          >
            All Companies
          </MenuItem>
          {filteredCompanyOptions.map((option) => (
            <MenuItem
              key={option}
              selected={companyFilter === option}
              onClick={() => {
                onCompanyFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "job_title") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search job titles")}
          <MenuItem
            selected={!jobTitleFilter}
            onClick={() => {
              onJobTitleFilterChange("");
              closeFilterMenu();
            }}
          >
            All Job Titles
          </MenuItem>
          {filteredJobTitleOptions.map((option) => (
            <MenuItem
              key={option}
              selected={jobTitleFilter === option}
              onClick={() => {
                onJobTitleFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  if (!hasContacts) {
    return (
      <Box
        sx={{
          mt: 4,
          py: 6,
          textAlign: "center",
          border: "1px dashed #ccc",
          borderRadius: 2,
          color: "text.secondary",
        }}
      >
        <Typography variant="h6" gutterBottom>
          No contacts found
        </Typography>
        <Typography variant="body2">
          This prospecting company does not have any contacts yet.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 1180, md: 1320 },
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow>
              {!embedded && (
                <TableCell padding="checkbox" align="center" sx={HEAD_CELL_SX}>
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={togglePage}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    indeterminateIcon={<RemoveCircleIcon />}
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                    }}
                  />
                </TableCell>
              )}

              {[
                { id: "name", label: "Name", sortable: true },
                { id: "company", label: "Company", sortable: true },
                { id: "job_title", label: "Job Title", sortable: true },
                { id: "direct_phone", label: "Direct Phone" },
                { id: "mobile_phone", label: "Mobile Phone" },
                { id: "email", label: "Email" },
                { id: "profile", label: "Profile" },
              ].map((col) => (
                <TableCell
                  key={col.id}
                  align="center"
                  sx={{
                    ...HEAD_CELL_SX,
                    ...(col.id === "name" && {
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }),
                    ...(col.id === "company" && {
                      width: COMPANY_COLUMN_WIDTH,
                      maxWidth: COMPANY_COLUMN_WIDTH,
                    }),
                    ...(col.id === "job_title" && {
                      width: JOB_TITLE_COLUMN_WIDTH,
                      maxWidth: JOB_TITLE_COLUMN_WIDTH,
                    }),
                    ...((col.id === "direct_phone" || col.id === "mobile_phone") && {
                      width: PHONE_COLUMN_WIDTH,
                      maxWidth: PHONE_COLUMN_WIDTH,
                    }),
                    ...(col.id === "email" && {
                      width: EMAIL_COLUMN_WIDTH,
                      maxWidth: EMAIL_COLUMN_WIDTH,
                    }),
                    ...(col.id === "profile" && {
                      width: PROFILE_COLUMN_WIDTH,
                      maxWidth: PROFILE_COLUMN_WIDTH,
                    }),
                  }}
                >
                  {col.id === "company"
                    ? renderFilterHeader({
                        label: col.label,
                        filterKey: "company",
                        sortable: true,
                        active: Boolean(companyFilter),
                        sortKey: "company",
                      })
                    : col.id === "job_title"
                      ? renderFilterHeader({
                          label: col.label,
                          filterKey: "job_title",
                          sortable: true,
                          active: Boolean(jobTitleFilter),
                          sortKey: "job_title",
                        })
                      : col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? orderDirection : "asc"}
                            onClick={() => onSort(col.id)}
                            sx={{
                              color: "#ffffff !important",
                              "&:hover": { color: "#ffffff !important" },
                              "&.Mui-active": { color: "#ffffff !important" },
                              "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                            }}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "white" }}>
            {paginatedContacts.map((c, index) => {
              const id = String(c.id);

              return (
                <TableRow
                  key={id}
                  sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: index % 2 === 0 ? "#fafafa" : "white", "&:hover": { backgroundColor: "#eef2f7" } }}
                >
                  {!embedded && (
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        checked={selectedIds.has(id)}
                        onChange={() => toggleOne(id)}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        sx={{
                          color: BRAND_BLUE,
                          "&.Mui-checked": { color: BRAND_BLUE },
                        }}
                      />
                    </TableCell>
                  )}

                  {/* NAME */}
                  <TableCell
                    sx={{ ...TABLE_STYLES.bodyCell, width: NAME_COLUMN_WIDTH, maxWidth: NAME_COLUMN_WIDTH }}
                    align="center"
                  >
                    <Tooltip title={c.name || ""}>
                      <Button
                        variant="text"
                        sx={TABLE_STYLES.clickableCell}
                        onClick={() => {
                          if (user?.id && addRecentItem) {
                            addRecentItem(user.id, "prospecting_contact", {
                              id: c.id,
                              name: c.name,
                            });
                          }
                          navigate(`/prospecting-contacts/${c.id}`);
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: NAME_COLUMN_WIDTH - 40,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.name}
                        </span>
                      </Button>
                    </Tooltip>
                  </TableCell>

                  {/* COMPANY */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: COMPANY_COLUMN_WIDTH, maxWidth: COMPANY_COLUMN_WIDTH }}
                  >
                    <Tooltip title={c.company?.name || ""}>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: COMPANY_COLUMN_WIDTH - 24,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        {c.company?.name || "—"}
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* JOB TITLE */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: JOB_TITLE_COLUMN_WIDTH, maxWidth: JOB_TITLE_COLUMN_WIDTH }}
                  >
                    <Tooltip title={normalizeJobTitle(c.job_title) || ""}>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: JOB_TITLE_COLUMN_WIDTH - 24,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        {normalizeJobTitle(c.job_title) || "—"}
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* DIRECT PHONE */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: PHONE_COLUMN_WIDTH, maxWidth: PHONE_COLUMN_WIDTH }}
                  >
                    {c.direct_phone_number
                      ? formatPhone(c.direct_phone_number)
                      : "—"}
                  </TableCell>

                  {/* MOBILE */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: PHONE_COLUMN_WIDTH, maxWidth: PHONE_COLUMN_WIDTH }}
                  >
                    {c.mobile_phone_number
                      ? formatPhone(c.mobile_phone_number)
                      : "—"}
                  </TableCell>

                  {/* EMAIL */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: EMAIL_COLUMN_WIDTH, maxWidth: EMAIL_COLUMN_WIDTH }}
                  >
                    {c.email ? (
                      <IconButton component="a" href={`mailto:${c.email}`}>
                        <EmailIcon sx={{ fontSize: 26, color: "#648315" }} />
                      </IconButton>
                    ) : (
                      <EmailIcon
                        sx={{
                          fontSize: 26,
                          color: "#648315",
                          opacity: 0.3,
                        }}
                      />
                    )}
                  </TableCell>

                  {/* PROFILE */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_STYLES.bodyCell, width: PROFILE_COLUMN_WIDTH, maxWidth: PROFILE_COLUMN_WIDTH }}
                  >
                    <IconButton
                      onClick={() => {
                        setActiveContact(c);
                        setProfileOpen(true);
                      }}
                    >
                      <AccountCircleIcon sx={{ color: "#648315", fontSize: 32 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {renderFilterMenu()}
    </>
  );
}

export default ProspectingContactsTable;
